// ---------------------------------------------------------------------------
// Speech capability note for Onboarding and Settings. When live Web Speech is
// unavailable (the desktop WebView), it checks whisperStatus(): with Whisper
// ready, the message reflects that transcription still happens on-device after
// Stop — the type/paste fallback is only mentioned when Whisper is missing too.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import { Alert, type SxProps } from '@mui/material';
import { isLiveTranscriptionSupported } from '../hooks/useSpeech';
import { whisperStatus, type WhisperStatus } from '../services/whisperService';

export default function SpeechStatusNote({ sx }: { sx?: SxProps }) {
  const liveSupported = isLiveTranscriptionSupported();
  const [whisper, setWhisper] = useState<WhisperStatus | null>(null);

  useEffect(() => {
    if (liveSupported) return;
    let cancelled = false;
    void whisperStatus().then((s) => {
      if (!cancelled) setWhisper(s);
    });
    return () => {
      cancelled = true;
    };
  }, [liveSupported]);

  if (liveSupported) return null;
  if (!whisper) return null; // status check in flight — avoid flashing the wrong copy

  if (whisper.ok) {
    return (
      <Alert severity="success" sx={sx}>
        Desktop speech-to-text: answers are audio-recorded, and the bundled on-device Whisper model
        transcribes them automatically after you press <strong>Stop</strong> — no typing needed. You can
        still edit the transcript before grading. (Live word-by-word transcription needs Chrome/Edge via{' '}
        <code>npm run dev</code>.)
      </Alert>
    );
  }

  return (
    <Alert severity="info" sx={sx}>
      Heads-up about speech: answers are <strong>audio-recorded for playback</strong>, but neither live
      speech-to-text nor on-device Whisper transcription is available here ({whisper.detail}) — you'll type
      or paste what you said before grading. For live transcription, run the app in Chrome/Edge with{' '}
      <code>npm run dev</code>.
    </Alert>
  );
}
